/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTrialCountdown, useTrialStatus } from '../../hooks/useTrialStatus';

interface TrialCountdownProps {
  /**
   * Display mode: compact (inline), card (standalone), or progress (bar only)
   */
  variant?: 'compact' | 'card' | 'progress';
  /**
   * Show detailed time breakdown (days, hours, minutes)
   */
  showDetails?: boolean;
  /**
   * Custom className for styling
   */
  className?: string;
}

/**
 * Component that displays a visual countdown of trial time remaining
 * Updates in real-time and shows progress with color-coded urgency
 */
export const TrialCountdown: React.FC<TrialCountdownProps> = ({
  variant = 'card',
  showDetails = true,
  className,
}) => {
  const { t } = useTranslation('billing');
  const theme = useTheme();

  const { warningLevel, planName, isTrialActive, daysRemaining } =
    useTrialStatus();
  const { timeRemaining, countdownText, isExpired } = useTrialCountdown();

  // Don't show if no trial active
  if (!isTrialActive) {
    return null;
  }

  // Calculate progress percentage (assuming 7-day trial)
  const totalTrialDays = 7; // This should come from backend/plan data
  const progressPercentage = Math.max(
    0,
    ((totalTrialDays - daysRemaining) / totalTrialDays) * 100
  );

  // Get color scheme based on warning level
  const getColors = () => {
    switch (warningLevel) {
      case 'expired':
        return {
          primary: theme.palette.error.main,
          background: theme.palette.error.light,
          icon: <ErrorIcon />,
        };
      case 'urgent':
        return {
          primary: theme.palette.error.main,
          background: theme.palette.error.light,
          icon: <WarningIcon />,
        };
      case 'warning':
        return {
          primary: theme.palette.warning.main,
          background: theme.palette.warning.light,
          icon: <WarningIcon />,
        };
      default:
        return {
          primary: theme.palette.info.main,
          background: theme.palette.info.light,
          icon: <ScheduleIcon />,
        };
    }
  };

  const colors = getColors();

  // Compact variant - inline display
  if (variant === 'compact') {
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {colors.icon}
        <Typography variant="body2" sx={{ color: colors.primary }}>
          {countdownText} {t('trial.remaining')}
        </Typography>
      </Box>
    );
  }

  // Progress variant - progress bar only
  if (variant === 'progress') {
    return (
      <Box className={className} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('trial.trialProgress')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.primary }}>
            {countdownText} {t('trial.remaining')}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
              backgroundColor: colors.primary,
            },
          }}
        />
      </Box>
    );
  }

  // Card variant - full display (default)
  return (
    <Card
      className={className}
      sx={{
        border: `2px solid ${colors.primary}`,
        backgroundColor: `${colors.background}20`, // 20% opacity
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {colors.icon}
          <Typography variant="h6" sx={{ color: colors.primary }}>
            {planName} {t('trial.trial')}
          </Typography>
          <Chip
            label={
              isExpired
                ? t('trial.expired')
                : `${daysRemaining} ${daysRemaining !== 1 ? t('trial.daysLeft') : t('trial.dayLeft')}`
            }
            size="small"
            sx={{
              backgroundColor: colors.primary,
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Box>

        {!isExpired && (
          <>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 12,
                borderRadius: 6,
                mb: 2,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors.primary,
                  borderRadius: 6,
                },
              }}
            />

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{ color: colors.primary, fontWeight: 'bold' }}
              >
                {countdownText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('trial.remaining')}
              </Typography>

              {showDetails && timeRemaining.total > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {timeRemaining.days > 0 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: colors.primary }}>
                        {timeRemaining.days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Day{timeRemaining.days !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  {(timeRemaining.hours > 0 || timeRemaining.days === 0) && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: colors.primary }}>
                        {timeRemaining.hours}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Hour{timeRemaining.hours !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  {timeRemaining.days === 0 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: colors.primary }}>
                        {timeRemaining.minutes}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Minute{timeRemaining.minutes !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}

        {isExpired && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ color: colors.primary, fontWeight: 'bold' }}
            >
              {t('trial.trialExpired')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('trial.maintainAccess')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TrialCountdown;
